import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Upload, X, ZoomIn, ShoppingCart, ExternalLink, Layers, Search, ChevronDown, ChevronRight, FolderOpen, FolderTree, Paperclip, FileText, Eye, Download, Trash2 } from 'lucide-react';

declare global {
  interface Window {
    Dropbox: {
      choose(options: {
        success: (files: Array<{ name: string; link: string }>) => void;
        linkType: 'preview' | 'direct';
        multiselect: boolean;
        folderselect: boolean;
      }): void;
    };
  }
}
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Card, CardContent } from '../../components/ui/card';
import { api } from '../../core/apiClient';
import apiClient from '../../core/apiClient';
import { formatCurrency } from '../../lib/utils';

interface Material {
  id: string;
  code?: string;
  name: string;
  unit: string;
  elementTypeId?: number;
  materialTypeId?: number;
  compositeCodeId?: number;
  compositeSubfamilyId?: number;
  compositeProductId?: number;
  compositeModelId?: number;
  compositeVariantId?: number;
  weightKgPerUnit: number;
  purchaseCost: number;
  purchaseCostUnit?: string;
  costPerUnit: number;
  pricePerKg: number;
  measurementFactor: number;
  priceFactor: number;
  wastePercentage: number;
  priceDate?: string;
  supplierRef?: string;
  location?: string;
  warehouseLocation?: string;
  supplierId?: string;
  observations?: string;
  imageUrl?: string | null;
  technicalImageUrl?: string | null;
  isActive: number;
  status?: string;
}

interface NodeAssociation {
  id: string;
  quantity: number;
  unit: string;
  measurement: number;
  node: {
    id: string;
    code?: string;
    name: string;
    nodeType: string;
  };
}

interface PurchaseHistory {
  id: string;
  quantity: number;
  unitType: string;
  unitPrice: number;
  discountPercent: number;
  lineTotal: number;
  description?: string;
  purchaseOrder: {
    id: string;
    orderNumber: string;
    orderDate: string;
    status: string;
    supplier?: { id: string; name: string; code: string };
  };
}

interface PriceHistoryEntry {
  id: number;
  pricePerMagnitude: number;
  purchaseCost: number;
  priceFactor: number;
  measurementFactor: number;
  wastePercentage: number;
  unit: string;
  priceDate: string | null;
  savedAt: string;
}

interface MaterialDocumentRow { id: string; name: string; fileName: string; fileSize: number; mimeType?: string; createdAt: string; }

interface SelectOption {
  id: number;
  code: string;
  name: string;
  elementTypeId?: number | null;
}

// Nivells 2/3/4 de l'Arbre d'elements (Subfamília/Producte/Model) — cadascú
// nomes referencia el seu pare immediat (veure AdminProductTree.tsx).
interface SubfamilyOption { id: number; code: string; name: string; compositeCodeId: number; isActive: number | boolean; }
interface ProductOption { id: number; code: string; name: string; subfamilyId: number; isActive: number | boolean; }
interface ModelOption { id: number; code: string; name?: string | null; productId: number; isActive: number | boolean; }
interface VariantOption { id: number; code: string; name?: string | null; modelId: number; isActive: number | boolean; }

// Arbre de codificació dels Elements — vegeu material.service.ts.getCodeTree().
// Nomes els dos primers nivells (Tipus d'Elements > Família d'Elements) tenen
// una relació real a BD; "combos" es la relació derivada (no FK) amb Tipus de
// Producte, calculada a partir dels materials que ja existeixen.
interface CodeTreeCombo {
  elementTypeId: number;
  materialTypeId: number;
  compositeCodeId: number;
  compositeCode: string;
  compositeCodeName: string;
  count: number;
}
interface CodeTree {
  elementTypes: { id: number; code: string; name: string }[];
  materialTypes: { id: number; code: string; name: string; elementTypeId: number | null }[];
  combos: CodeTreeCombo[];
}

interface UnitType {
  code: string;
  name: string;
  description?: string;
}

interface Supplier {
  id: string;
  code: string;
  name: string;
}

const emptyMaterial: Partial<Material> = {
  name: '',
  unit: 'Ut',
  elementTypeId: 1,
  weightKgPerUnit: 0,
  purchaseCost: 0,
  purchaseCostUnit: 'Kg',
  measurementFactor: 1,
  priceFactor: 1,
  wastePercentage: 0,
  observations: '',
  imageUrl: null,
  technicalImageUrl: null,
  status: 'habitual',
};

export default function MaterialForm() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';

  const [material, setMaterial] = useState<Partial<Material>>(emptyMaterial);
  const [unitTypes, setUnitTypes] = useState<UnitType[]>([]);
  const [elementTypes, setElementTypes] = useState<SelectOption[]>([]);
  const [materialTypes, setMaterialTypes] = useState<SelectOption[]>([]);
  const [compositeCodes, setCompositeCodes] = useState<SelectOption[]>([]);
  const [compositeSubfamilies, setCompositeSubfamilies] = useState<SubfamilyOption[]>([]);
  const [compositeProducts, setCompositeProducts] = useState<ProductOption[]>([]);
  const [compositeModels, setCompositeModels] = useState<ModelOption[]>([]);
  const [compositeVariants, setCompositeVariants] = useState<VariantOption[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nodeAssociations, setNodeAssociations] = useState<NodeAssociation[]>([]);
  const [nodesLoading, setNodesLoading] = useState(false);
  const [purchaseHistory, setPurchaseHistory] = useState<PurchaseHistory[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [priceHistory, setPriceHistory] = useState<PriceHistoryEntry[]>([]);
  const [documents, setDocuments] = useState<MaterialDocumentRow[]>([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docsDragOver, setDocsDragOver] = useState(false);
  const [codeTree, setCodeTree] = useState<CodeTree | null>(null);
  const [codeTreeLoading, setCodeTreeLoading] = useState(false);
  const [expandedElementTypes, setExpandedElementTypes] = useState<Set<number>>(new Set());
  const [expandedMaterialTypes, setExpandedMaterialTypes] = useState<Set<number>>(new Set());
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<'dades' | 'escandalls' | 'compres' | 'documents' | 'arbre'>('dades');

  const [unitOpen, setUnitOpen] = useState(false);

  // Supplier search state
  const [supplierQuery, setSupplierQuery] = useState('');
  const [supplierSuggestions, setSupplierSuggestions] = useState<Supplier[]>([]);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const supplierTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const supplierRef = useRef<HTMLDivElement>(null);

  // Image upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imageDragOver, setImageDragOver] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const technicalFileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingTechnicalImage, setUploadingTechnicalImage] = useState(false);
  const [technicalImageDragOver, setTechnicalImageDragOver] = useState(false);
  const [technicalLightboxOpen, setTechnicalLightboxOpen] = useState(false);

  // Dropbox Chooser
  const [dropboxReady, setDropboxReady] = useState(false);

  const fetchOptions = useCallback(async () => {
    const empty = { success: true, data: [] };
    const [etRes, mtRes, ccRes, utRes, subRes, prodRes, modelRes, variantRes] = await Promise.all([
      api.get<{ success: boolean; data: SelectOption[] }>('/simple-materials/element-types').catch(() => empty),
      api.get<{ success: boolean; data: SelectOption[] }>('/simple-materials/material-types').catch(() => empty),
      api.get<{ success: boolean; data: SelectOption[] }>('/simple-materials/composite-codes').catch(() => empty),
      api.get<{ success: boolean; data: UnitType[] }>('/nodes/unit-types').catch(() => empty),
      api.get<{ success: boolean; data: SubfamilyOption[] }>('/catalog/composite-subfamilies').catch(() => empty),
      api.get<{ success: boolean; data: ProductOption[] }>('/catalog/composite-products').catch(() => empty),
      api.get<{ success: boolean; data: ModelOption[] }>('/catalog/composite-models').catch(() => empty),
      api.get<{ success: boolean; data: VariantOption[] }>('/catalog/composite-variants').catch(() => empty),
    ]);
    setElementTypes(etRes.data as SelectOption[]);
    setMaterialTypes(mtRes.data as SelectOption[]);
    setCompositeCodes(ccRes.data as SelectOption[]);
    setUnitTypes(utRes.data as UnitType[]);
    setCompositeSubfamilies((subRes.data as SubfamilyOption[]).filter((s) => Number(s.isActive) === 1));
    setCompositeProducts((prodRes.data as ProductOption[]).filter((p) => Number(p.isActive) === 1));
    setCompositeModels((modelRes.data as ModelOption[]).filter((m) => Number(m.isActive) === 1));
    setCompositeVariants((variantRes.data as VariantOption[]).filter((v) => Number(v.isActive) === 1));
  }, []);

  const searchSuppliers = useCallback(async (q: string) => {
    try {
      const trimmed = q.trim();
      const limit = trimmed ? 100 : 2000;
      const params = new URLSearchParams({ limit: String(limit) });
      if (trimmed) params.set('search', trimmed);
      const res = await api.get<{ success: boolean; data: Supplier[] }>(
        `/suppliers?${params}`
      );
      setSupplierSuggestions(res.data ?? []);
    } catch {
      setSupplierSuggestions([]);
    }
  }, []);

  const fetchMaterial = useCallback(async () => {
    if (isNew) return;
    try {
      setLoading(true);
      const [matRes, phRes] = await Promise.all([
        api.get<{ success: boolean; data: Material }>(`/simple-materials/${id}`),
        api.get<{ success: boolean; data: PriceHistoryEntry[] }>(`/simple-materials/${id}/price-history`).catch(() => ({ data: [] as PriceHistoryEntry[] })),
      ]);
      const mat = matRes.data;
      setMaterial({ ...mat, supplierId: mat.supplierId ?? (mat as any).supplier?.id });
      setPriceHistory(phRes.data ?? []);
    } catch (err) {
      console.error('[v0] Error fetching material:', err);
      setError('Error en carregar el material');
    } finally {
      setLoading(false);
    }
  }, [id, isNew]);

  useEffect(() => {
    fetchOptions();
    fetchMaterial();
  }, [fetchOptions, fetchMaterial]);

  // Cargar el proveedor seleccionado cuando el material ya tiene supplierId
  useEffect(() => {
    if (material.supplierId && !selectedSupplier) {
      api.get<{ success: boolean; data: Supplier }>(`/suppliers/${material.supplierId}`)
        .then((r) => {
          setSelectedSupplier(r.data);
          setSupplierQuery(r.data.name);
        })
        .catch(() => {});
    }
  }, [material.supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (supplierRef.current && !supplierRef.current.contains(e.target as Node)) {
        setSupplierOpen(false);
        // Si hay un proveedor seleccionado, restaurar el nombre en el input
        if (selectedSupplier) setSupplierQuery(selectedSupplier.name);
        else setSupplierQuery('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [selectedSupplier]);

  useEffect(() => {
    if (!isNew && id) {
      setNodesLoading(true);
      api.get<{ success: boolean; data: NodeAssociation[] }>(`/simple-materials/${id}/nodes`)
        .then((r) => setNodeAssociations(r.data ?? []))
        .catch(() => {})
        .finally(() => setNodesLoading(false));

      setHistoryLoading(true);
      api.get<{ success: boolean; data: PurchaseHistory[] }>(`/simple-materials/${id}/purchases`)
        .then((r) => setPurchaseHistory(r.data ?? []))
        .catch(() => {})
        .finally(() => setHistoryLoading(false));
    }
  }, [id, isNew]);

  // ── Documents adjunts a la fitxa del material ────────────────────────────

  const fetchDocuments = useCallback(async () => {
    if (isNew || !id) return;
    try {
      const res = await api.get<{ success: boolean; data: MaterialDocumentRow[] }>(`/simple-materials/${id}/documents`);
      setDocuments(res.data ?? []);
    } catch { /* no bloquejar la resta del formulari si falla el llistat */ }
  }, [id, isNew]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);

  const handleUploadDocument = async (file: File) => {
    if (isNew || !id || !file) return;
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', file.name);
      await apiClient.post(`/simple-materials/${id}/documents`, fd, { headers: { 'Content-Type': undefined } });
      fetchDocuments();
    } catch (err: any) {
      alert('Error pujant el document: ' + (err?.response?.data?.message ?? err?.message));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDocumentsDrop = (e: React.DragEvent<HTMLLabelElement>) => {
    e.preventDefault();
    setDocsDragOver(false);
    Array.from(e.dataTransfer.files ?? []).forEach((f) => handleUploadDocument(f));
  };

  const handleDeleteDocument = async (docId: string) => {
    if (isNew || !id) return;
    if (!confirm('Eliminar aquest document?')) return;
    try {
      await api.delete(`/simple-materials/${id}/documents/${docId}`);
      setDocuments((prev) => prev.filter((d) => d.id !== docId));
    } catch { alert('Error eliminant el document'); }
  };

  // Les rutes de documents requereixen el token d'autenticació (Authorization
  // header), que un <a href> o window.open directe no envia — es baixa com a
  // blob i s'obre/descarrega des d'aquí.
  const handleOpenDocument = async (doc: MaterialDocumentRow, download: boolean) => {
    try {
      const res = await apiClient.get(`/simple-materials/${id}/documents/${doc.id}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: doc.mimeType || 'application/octet-stream' }));
      if (download) {
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.name;
        a.click();
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert('Error obrint el document');
    }
  };

  const fmtFileSize = (bytes: number) => {
    if (!bytes) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
  };

  // ── Arbre d'elements — carrega perezosa (dades del catàleg, no d'aquest
  // material en concret), en obrir la pestanya per primer cop. En arribar,
  // desplega automàticament la branca d'aquest material perquè es vegi on cau.
  useEffect(() => {
    if (activeTab !== 'arbre' || codeTree || codeTreeLoading) return;
    setCodeTreeLoading(true);
    api.get<{ success: boolean; data: CodeTree }>('/simple-materials/code-tree')
      .then((r) => {
        setCodeTree(r.data);
        if (material.elementTypeId) setExpandedElementTypes(new Set([material.elementTypeId]));
        if (material.materialTypeId) setExpandedMaterialTypes(new Set([material.materialTypeId]));
      })
      .catch(() => {})
      .finally(() => setCodeTreeLoading(false));
  }, [activeTab, codeTree, codeTreeLoading, material.elementTypeId, material.materialTypeId]);

  useEffect(() => {
    api.get<{ success: boolean; data: { appKey: string } }>('/admin/settings/dropbox')
      .then((r) => {
        const key = r.data?.appKey;
        if (!key) return;
        if (document.getElementById('dropboxjs')) { setDropboxReady(true); return; }
        const script = document.createElement('script');
        script.id = 'dropboxjs';
        script.src = 'https://www.dropbox.com/static/api/2/dropins.js';
        script.setAttribute('data-app-key', key);
        script.onload = () => setDropboxReady(true);
        document.head.appendChild(script);
      })
      .catch(() => {});
  }, []);

  const handleDropboxChoose = () => {
    if (!window.Dropbox) return;
    window.Dropbox.choose({
      success: (files) => {
        const file = files[0];
        setMaterial((prev) => ({
          ...prev,
          location: JSON.stringify({ name: file.name, link: file.link }),
        }));
      },
      linkType: 'preview',
      multiselect: false,
      folderselect: true,
    });
  };

  const goBack = () => {
    localStorage.setItem('compras-activeTab', 'materials');
    navigate('/compres');
  };

  const handleImageUpload = async (file: File, field: 'imageUrl' | 'technicalImageUrl' = 'imageUrl') => {
    if (!file) return;
    const setUploading = field === 'imageUrl' ? setUploadingImage : setUploadingTechnicalImage;
    setUploading(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiClient.post<{ success: boolean; data: { imageUrl: string } }>(
        '/simple-materials/upload-image',
        fd,
        { headers: { 'Content-Type': undefined } },
      );
      setMaterial((prev) => ({ ...prev, [field]: res.data.data.imageUrl }));
    } catch (err: any) {
      setError(err?.response?.data?.message ?? err?.message ?? 'Error en pujar la imatge');
    } finally {
      setUploading(false);
    }
  };

  const handleImageDrop = (e: React.DragEvent<HTMLDivElement>, field: 'imageUrl' | 'technicalImageUrl' = 'imageUrl') => {
    e.preventDefault();
    if (field === 'imageUrl') setImageDragOver(false); else setTechnicalImageDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleImageUpload(file, field);
  };

  const handleSave = async () => {
    if (!material.name?.trim()) {
      setError('El nom es obligatori');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload: Record<string, unknown> = {
        name: material.name,
        unit: material.unit,
        elementTypeId: material.elementTypeId,
        weightKgPerUnit: material.weightKgPerUnit,
        purchaseCost: material.purchaseCost,
        purchaseCostUnit: material.purchaseCostUnit || 'Kg',
        measurementFactor: material.measurementFactor,
        priceFactor: material.priceFactor,
        wastePercentage: material.wastePercentage,
        imageUrl: material.imageUrl ?? null,
        technicalImageUrl: material.technicalImageUrl ?? null,
      };
      if (material.code) payload.code = material.code;
      if (material.materialTypeId) payload.materialTypeId = material.materialTypeId;
      if (material.compositeCodeId) payload.compositeCodeId = material.compositeCodeId;
      if (material.compositeSubfamilyId) payload.compositeSubfamilyId = material.compositeSubfamilyId;
      if (material.compositeProductId) payload.compositeProductId = material.compositeProductId;
      if (material.compositeModelId) payload.compositeModelId = material.compositeModelId;
      if (material.compositeVariantId) payload.compositeVariantId = material.compositeVariantId;
      if (material.priceDate) payload.priceDate = material.priceDate;
      if (material.supplierRef) payload.supplierRef = material.supplierRef;
      if (material.location) payload.location = material.location;
      if (material.warehouseLocation) payload.warehouseLocation = material.warehouseLocation;
      if (material.supplierId) payload.supplierId = material.supplierId;
      if (material.observations) payload.observations = material.observations;

      if (isNew) {
        await api.post('/simple-materials', payload);
      } else {
        await api.put(`/simple-materials/${id}`, payload);
        // Refrescar historial de preus abans de navegar
        api.get<{ success: boolean; data: PriceHistoryEntry[] }>(`/simple-materials/${id}/price-history`)
          .then(r => setPriceHistory(r.data ?? []))
          .catch(() => {});
      }
      goBack();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { error?: { message?: string } } } };
      setError(e.response?.data?.error?.message || 'Error en guardar el material');
    } finally {
      setSaving(false);
    }
  };

  const statusLabel: Record<string, { label: string; cls: string }> = {
    DRAFT:              { label: 'Esborrany',    cls: 'bg-gray-100 text-gray-600' },
    SENT:               { label: 'Enviada',      cls: 'bg-indigo-100 text-indigo-700' },
    PARTIALLY_RECEIVED: { label: 'Rec. parcial', cls: 'bg-orange-100 text-orange-700' },
    RECEIVED:           { label: 'Rebuda',       cls: 'bg-green-100 text-green-700' },
    CANCELLED:          { label: 'Cancel·lada',  cls: 'bg-red-100 text-red-600' },
  };

  const fmtNum = (n: number) =>
    n.toLocaleString('ca-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Camps numèrics: coma decimal, punt de milers (locale es-ES)
  const numProps = (
    key: string,
    displayValue: number,
    decimals: number,
    onCommit: (n: number) => void,
  ) => {
    const fmt = (n: number) =>
      Number(n || 0).toLocaleString('ca-ES', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: false,
      });
    return {
      type: 'text' as const,
      inputMode: 'decimal' as const,
      value: key in drafts ? drafts[key] : fmt(displayValue),
      onFocus: (e: React.FocusEvent<HTMLInputElement>) => {
        setDrafts(d => ({ ...d, [key]: fmt(displayValue) }));
        e.target.select();
      },
      onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
        setDrafts(d => ({ ...d, [key]: e.target.value })),
      onBlur: () => {
        let raw = (drafts[key] ?? '').trim();
        // Si té coma: la coma és decimal, els punts són milers → eliminem punts i canviem coma per punt
        // Si no té coma i té punt: el punt és decimal (entrada en anglès, s'accepta)
        if (raw.includes(',')) raw = raw.replace(/\./g, '').replace(',', '.');
        const parsed = parseFloat(raw);
        onCommit(isNaN(parsed) ? 0 : parsed);
        setDrafts(d => { const n = { ...d }; delete n[key]; return n; });
      },
    };
  };

  // Derived values (live) — Number() guards against string values from the API
  const measurementFactor = Number(material.measurementFactor || 1);
  const priceFactor       = Number(material.priceFactor       || 1);
  const weightKgPerUnit   = Number(material.weightKgPerUnit   || 0);
  const purchaseCost      = Number(material.purchaseCost      || 0);
  const wastePercentage   = Number(material.wastePercentage   || 0);

  // Pes per unitat de compra: [Pes_Element] × [Factor_unitat_medicio]
  const calculatedWeight = weightKgPerUnit * measurementFactor;
  // Cost per unitat de compra (€/ut): [Cost_Ut_Compra] × [Factor] × [Factor_unitat_medicio]
  const calculatedCost = purchaseCost * priceFactor * measurementFactor;
  // Preu/Kg: [Cost_Ut_Compra] × [Factor] / [Pes_Element]
  const calculatedPricePerKg = weightKgPerUnit > 0 ? purchaseCost * priceFactor / weightKgPerUnit : 0;
  // Preu final (€/unitat de mesura): per no perdre marge amb el material que es
  // perd en la merma, cal dividir pel rendiment real (1 - merma%), no multiplicar.
  const wasteFactor         = wastePercentage < 100 ? 1 / (1 - wastePercentage / 100) : 1;
  const totalWithWaste      = calculatedCost * wasteFactor;
  const wasteImpact         = totalWithWaste - calculatedCost;
  // Preu per unitat de mesura final (€/unit): cost_compra × [Factor] / (1 - merma%)
  const pricePerMagnitude   = purchaseCost * priceFactor * wasteFactor;
  const pricePerKgWithWaste = calculatedWeight > 0 ? totalWithWaste / calculatedWeight : 0;

  if (loading) {
    return <div className="p-8 text-center">Carregant...</div>;
  }

  // Quan els tres catàlegs estan informats el backend sempre recalcula el
  // codi compost (Tipus d'element.Família.Tipus de producte.Seqüencial) i
  // descarta qualsevol valor manual — el camp passa a ser de nomes lectura
  // perque editar-lo a mà (p.ex. per esquivar un error de duplicat) va
  // corrompre seqüencials (1.110.0.311 → 3111 → 31111...).
  const isComposedCodeAuto = !!(material.elementTypeId && material.materialTypeId && material.compositeCodeId);

  return (
    <div className="space-y-6">
      {/* Capçalera sticky amb botons d'acció sempre visibles */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur border-b flex items-center justify-between gap-4 py-3 -mx-4 px-4 md:-mx-6 md:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={goBack}
            className="p-2 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <h1 className="text-2xl font-semibold text-foreground truncate">
              {isNew ? 'Nou Material' : 'Editar Material'}
            </h1>
            {material.code && (
              <span className="font-mono text-xs text-muted-foreground bg-slate-100 px-2 py-0.5 rounded flex-shrink-0">
                {material.code}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button type="button" onClick={goBack} className="px-4 py-2 border border-gray-200 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
            Cancel·lar
          </button>
          <button type="button" onClick={handleSave} disabled={saving || !material.name} className="flex items-center gap-2 px-4 py-2 bg-[#2563EB] text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50">
            <Save className="h-4 w-4" />
            {saving ? 'Desant...' : 'Desa'}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Pestanyes — només visibles en mode edició (mateix patró de botons que NodeForm) */}
      {!isNew && (
        <div className="grid grid-cols-5 gap-3">
          <button
            type="button"
            onClick={() => setActiveTab('dades')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'dades' ? 'border-blue-500 bg-blue-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-blue-500/10 shrink-0"><FileText className="h-4 w-4 text-blue-500" /></div>
            <span className="text-xs font-semibold truncate">Dades</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('escandalls')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'escandalls' ? 'border-teal-500 bg-teal-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-teal-500/10 shrink-0"><Layers className="h-4 w-4 text-teal-500" /></div>
            <span className="text-xs font-semibold truncate">Escandalls{nodeAssociations.length ? ` (${nodeAssociations.length})` : ''}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('compres')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'compres' ? 'border-amber-500 bg-amber-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-amber-500/10 shrink-0"><ShoppingCart className="h-4 w-4 text-amber-500" /></div>
            <span className="text-xs font-semibold truncate">Historial de compres{purchaseHistory.length ? ` (${purchaseHistory.length})` : ''}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('documents')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'documents' ? 'border-rose-500 bg-rose-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-rose-500/10 shrink-0"><Paperclip className="h-4 w-4 text-rose-500" /></div>
            <span className="text-xs font-semibold truncate">Documentació{documents.length ? ` (${documents.length})` : ''}</span>
          </button>
          <button
            type="button"
            onClick={() => setActiveTab('arbre')}
            className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border transition-all ${activeTab === 'arbre' ? 'border-indigo-500 bg-indigo-50/50' : 'border-border bg-white hover:shadow-sm'}`}
          >
            <div className="p-1.5 rounded-lg bg-indigo-500/10 shrink-0"><FolderTree className="h-4 w-4 text-indigo-500" /></div>
            <span className="text-xs font-semibold truncate">Arbre d'elements</span>
          </button>
        </div>
      )}

      {/* Pestanya Dades */}
      {(isNew || activeTab === 'dades') && <Card className="bg-muted/30 [&_input:not(.hidden)]:bg-white [&_select]:bg-white [&_textarea]:bg-white"><CardContent className="p-6 overflow-x-auto">
      <div className="flex gap-6 items-stretch min-w-[860px]">

        {/* Left: form fields */}
        <div className="flex-1 min-w-[380px] space-y-4">

          {/* Codi / Nom / Unitat */}
          <div className="flex gap-3 items-end">
            <div className="space-y-2 shrink-0 w-24">
              <Label>Codi</Label>
              <Input
                value={material.code || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMaterial({ ...material, code: e.target.value })
                }
                placeholder="Auto"
                readOnly={isComposedCodeAuto}
                title={isComposedCodeAuto ? "Codi calculat automàticament a partir de Tipus d'element/Família/Tipus de producte" : undefined}
                className={isComposedCodeAuto ? 'bg-muted/50 text-muted-foreground cursor-not-allowed' : undefined}
              />
            </div>
            <div className="space-y-2 flex-1 min-w-0">
              <Label>Nom *</Label>
              <Input
                value={material.name || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMaterial({ ...material, name: e.target.value })
                }
              />
            </div>
            <div className="space-y-2 shrink-0 w-32">
              <Label>Ut.Amidament</Label>
              <div className="relative">
                <button
                  type="button"
                  className="w-full h-9 px-3 text-sm border border-input rounded-md bg-background flex items-center justify-between gap-1 focus:outline-none focus:ring-2 focus:ring-ring"
                  onClick={() => setUnitOpen(o => !o)}
                  onBlur={() => setTimeout(() => setUnitOpen(false), 150)}
                >
                  <span className="font-medium">{(material.unit || 'Ut').toLowerCase()}</span>
                  <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                </button>
                {unitOpen && (
                  <ul className="absolute z-50 left-0 min-w-full w-max mt-1 max-h-72 overflow-y-auto bg-background border border-input rounded-md shadow-lg text-sm">
                    {unitTypes.map((ut) => (
                      <li
                        key={ut.code}
                        className={`px-3 py-2 cursor-pointer hover:bg-accent whitespace-nowrap ${material.unit === ut.code ? 'bg-accent/60 font-medium' : ''}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setMaterial({ ...material, unit: ut.code });
                          setUnitOpen(false);
                        }}
                      >
                        <span className="font-mono font-medium">{ut.code.toLowerCase()}</span>
                        <span className="text-muted-foreground ml-2">— {ut.description ?? ut.name}</span>
                      </li>
                    ))}
                    {material.unit && !unitTypes.some((ut) => ut.code === material.unit) && (
                      <li className="px-3 py-2 cursor-pointer hover:bg-accent bg-accent/60 font-medium whitespace-nowrap">
                        {material.unit.toLowerCase()}
                      </li>
                    )}
                  </ul>
                )}
              </div>
            </div>
          </div>

          {/* Tipus d'elements / Família d'elements / Producte */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{"Tipus d'elements"}</Label>
              <Select
                value={String(material.elementTypeId || '')}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newElementTypeId = parseInt(e.target.value, 10) || undefined;
                  // Clear materialTypeId if it doesn't belong to the new element type
                  const currentMtId = material.materialTypeId;
                  const isCompatible = currentMtId
                    ? materialTypes.some(mt => mt.id === currentMtId && mt.elementTypeId === newElementTypeId)
                    : true;
                  setMaterial({
                    ...material,
                    elementTypeId: newElementTypeId,
                    materialTypeId: isCompatible ? currentMtId : undefined,
                  });
                }}
              >
                <option value="">Selecciona...</option>
                {elementTypes.map((et) => (
                  <option key={et.id} value={et.id}>{et.code} - {et.name}</option>
                ))}
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{"Família d'elements"}</Label>
              <Select
                value={String(material.materialTypeId || '')}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setMaterial({ ...material, materialTypeId: parseInt(e.target.value, 10) || undefined })
                }
              >
                <option value="">Selecciona...</option>
                {materialTypes
                  .filter(mt => !material.elementTypeId || mt.elementTypeId === material.elementTypeId)
                  .map((mt) => (
                    <option key={mt.id} value={mt.id}>{mt.code} - {mt.name}</option>
                  ))
                }
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Tipus de producte</Label>
              <Select
                value={String(material.compositeCodeId || '')}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newCompositeCodeId = parseInt(e.target.value, 10) || undefined;
                  setMaterial({
                    ...material,
                    compositeCodeId: newCompositeCodeId,
                    compositeSubfamilyId: undefined,
                    compositeProductId: undefined,
                    compositeModelId: undefined,
                    compositeVariantId: undefined,
                  });
                }}
              >
                <option value="">Selecciona...</option>
                {compositeCodes.map((cc) => (
                  <option key={cc.id} value={cc.id}>{cc.code} - {cc.name}</option>
                ))}
              </Select>
            </div>
          </div>

          {/* Subfamília / Producte / Model / Variant — nivells 2-5 de l'Arbre
              d'elements, en cascada sota Tipus de producte (Família); opcionals. */}
          <div className="grid grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Subfamília</Label>
              <Select
                value={String(material.compositeSubfamilyId || '')}
                disabled={!material.compositeCodeId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newSubfamilyId = parseInt(e.target.value, 10) || undefined;
                  setMaterial({
                    ...material,
                    compositeSubfamilyId: newSubfamilyId,
                    compositeProductId: undefined,
                    compositeModelId: undefined,
                    compositeVariantId: undefined,
                  });
                }}
              >
                <option value="">Selecciona...</option>
                {compositeSubfamilies
                  .filter((s) => !material.compositeCodeId || s.compositeCodeId === material.compositeCodeId)
                  .map((s) => (
                    <option key={s.id} value={s.id}>{s.code} - {s.name}</option>
                  ))
                }
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Producte</Label>
              <Select
                value={String(material.compositeProductId || '')}
                disabled={!material.compositeSubfamilyId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newProductId = parseInt(e.target.value, 10) || undefined;
                  setMaterial({ ...material, compositeProductId: newProductId, compositeModelId: undefined, compositeVariantId: undefined });
                }}
              >
                <option value="">Selecciona...</option>
                {compositeProducts
                  .filter((p) => !material.compositeSubfamilyId || p.subfamilyId === material.compositeSubfamilyId)
                  .map((p) => (
                    <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
                  ))
                }
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Model</Label>
              <Select
                value={String(material.compositeModelId || '')}
                disabled={!material.compositeProductId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                  const newModelId = parseInt(e.target.value, 10) || undefined;
                  setMaterial({ ...material, compositeModelId: newModelId, compositeVariantId: undefined });
                }}
              >
                <option value="">Selecciona...</option>
                {compositeModels
                  .filter((m) => !material.compositeProductId || m.productId === material.compositeProductId)
                  .map((m) => (
                    <option key={m.id} value={m.id}>{m.code}{m.name ? ` - ${m.name}` : ''}</option>
                  ))
                }
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Variant</Label>
              <Select
                value={String(material.compositeVariantId || '')}
                disabled={!material.compositeModelId}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  setMaterial({ ...material, compositeVariantId: parseInt(e.target.value, 10) || undefined })
                }
              >
                <option value="">Selecciona...</option>
                {compositeVariants
                  .filter((v) => !material.compositeModelId || v.modelId === material.compositeModelId)
                  .map((v) => (
                    <option key={v.id} value={v.id}>{v.code}{v.name ? ` - ${v.name}` : ''}</option>
                  ))
                }
              </Select>
            </div>
          </div>

          {/* Fila 1: Pes · F.Amidament · Cost compra (156px fix) · Merma */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-2">
              <Label>Pes</Label>
              <div className="relative">
                <Input
                  className="pr-20"
                  {...numProps(
                    'weightKgPerUnit',
                    Number(material.weightKgPerUnit || 0),
                    3,
                    val => setMaterial(m => ({ ...m, weightKgPerUnit: val })),
                  )}
                />
                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
                  kg/{material.unit || 'ut'}
                </span>
              </div>
            </div>
            <div className="flex-1 space-y-2">
              <Label>Factor d'amidament</Label>
              <div className="relative">
                <Input
                  className="pr-14"
                  {...numProps(
                    'measurementFactor',
                    Number(material.measurementFactor || 1),
                    3,
                    val => setMaterial(m => ({ ...m, measurementFactor: val || 1 })),
                  )}
                />
                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
                  {material.unit || 'ut'}/ut
                </span>
              </div>
            </div>
            <div className="shrink-0 space-y-2" style={{ width: '156px' }}>
              <Label>Cost compra</Label>
              <div className="flex items-stretch">
                <Input
                  className="rounded-r-none flex-1 min-w-0"
                  {...numProps(
                    'purchaseCost',
                    Number(material.purchaseCost || 0),
                    2,
                    val => setMaterial(m => ({ ...m, purchaseCost: val })),
                  )}
                />
                <div className="flex items-center gap-0.5 border border-l-0 rounded-r-md bg-muted/50 px-2 shrink-0">
                  <span className="text-xs text-muted-foreground">€/</span>
                  <select
                    value={material.purchaseCostUnit || 'Kg'}
                    onChange={(e) => setMaterial(m => ({ ...m, purchaseCostUnit: e.target.value }))}
                    className="border-0 bg-transparent text-xs text-muted-foreground focus:outline-none cursor-pointer"
                  >
                    {unitTypes.map(ut => <option key={ut.code} value={ut.code}>{ut.code.toLowerCase()}</option>)}
                  </select>
                </div>
              </div>
            </div>
            <div className="w-36 space-y-2">
              <Label>Factor compra</Label>
              <div className="relative">
                <Input
                  className="pr-14"
                  {...numProps(
                    'priceFactor',
                    Number(material.priceFactor || 1),
                    3,
                    val => setMaterial(m => ({ ...m, priceFactor: val || 1 })),
                  )}
                />
                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
                  {(material.purchaseCostUnit || 'kg').toLowerCase()}/{(material.unit || 'ut').toLowerCase()}
                </span>
              </div>
            </div>
          </div>

          {/* Fila 2: Merma · Data Preu */}
          <div className="flex gap-3">
            <div className="flex-1 space-y-2">
              <Label>Merma</Label>
              <div className="relative">
                <Input
                  className="pr-6"
                  {...numProps(
                    'wastePercentage',
                    Number(material.wastePercentage || 0) / 100,
                    3,
                    val => setMaterial(m => ({ ...m, wastePercentage: val * 100 })),
                  )}
                />
                <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs text-muted-foreground">
                  %
                </span>
              </div>
            </div>
            <div className="space-y-2" style={{ width: '163px' }}>
              <Label>Data Preu</Label>
              <Input
                type="date"
                value={material.priceDate ? material.priceDate.substring(0, 10) : ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMaterial({ ...material, priceDate: e.target.value })
                }
              />
            </div>
          </div>

          {/* Proveïdor / Ref. Proveïdor / Localització */}
          <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2 space-y-2" ref={supplierRef}>
              <Label>Proveïdor</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                <input
                  type="text"
                  className="w-full h-9 pl-8 pr-8 text-sm border border-input rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Cercar proveïdor..."
                  value={supplierQuery}
                  onChange={(e) => {
                    const q = e.target.value;
                    setSupplierQuery(q);
                    setSupplierOpen(true);
                    if (supplierTimer.current) clearTimeout(supplierTimer.current);
                    supplierTimer.current = setTimeout(() => searchSuppliers(q), 200);
                  }}
                  onFocus={() => {
                    setSupplierOpen(true);
                    searchSuppliers(supplierQuery);
                  }}
                />
                {selectedSupplier ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    onClick={() => {
                      setSelectedSupplier(null);
                      setSupplierQuery('');
                      setMaterial({ ...material, supplierId: undefined });
                      setSupplierSuggestions([]);
                    }}
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                ) : (
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                )}
                {supplierOpen && supplierSuggestions.length > 0 && (
                  <ul className="absolute z-50 left-0 min-w-full w-max mt-1 max-h-72 overflow-y-auto bg-background border border-input rounded-md shadow-lg text-sm">
                    {supplierSuggestions.map((s) => (
                      <li
                        key={s.id}
                        className={`px-3 py-2 cursor-pointer hover:bg-accent flex items-center gap-2 whitespace-nowrap ${s.id === material.supplierId ? 'bg-accent/60 font-medium' : ''}`}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedSupplier(s);
                          setSupplierQuery(s.name);
                          setMaterial({ ...material, supplierId: s.id });
                          setSupplierOpen(false);
                        }}
                      >
                        <span className="font-mono text-xs text-muted-foreground w-16 shrink-0">{s.code}</span>
                        <span>{s.name}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {supplierOpen && supplierSuggestions.length === 0 && supplierQuery.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 mt-1 bg-background border border-input rounded-md shadow-lg px-3 py-2 text-sm text-muted-foreground">
                    Sense resultats
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Ref. Proveïdor</Label>
              <Input
                value={material.supplierRef || ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMaterial({ ...material, supplierRef: e.target.value })
                }
                className="w-40"
              />
            </div>
          </div>
            <div className="flex gap-3 items-end">
              <div className="w-48 shrink-0">
                <Label>Localització</Label>
                <Input
                  value={material.warehouseLocation || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setMaterial({ ...material, warehouseLocation: e.target.value })
                  }
                  placeholder="Ubicació al magatzem"
                />
              </div>
              <div className="flex-1 min-w-0 space-y-2">
                <Label>Carpeta Dropbox</Label>
                <div className="flex gap-1.5">
                  <div className="flex-1 flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30 text-xs min-h-[38px] overflow-hidden">
                    {material.location ? (() => {
                      try {
                        const loc: { name: string; link: string } = JSON.parse(material.location);
                        return (
                          <>
                            <FolderOpen className="h-4 w-4 text-blue-500 shrink-0" />
                            <span className="truncate">{loc.name}</span>
                          </>
                        );
                      } catch {
                        return <span className="text-muted-foreground truncate">{material.location}</span>;
                      }
                    })() : (
                      <span className="text-muted-foreground">Cap carpeta seleccionada</span>
                    )}
                  </div>
                  {dropboxReady && (
                    <button
                      type="button"
                      onClick={handleDropboxChoose}
                      className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors text-xs flex items-center gap-1.5 shrink-0"
                      title="Seleccionar carpeta de Dropbox"
                    >
                      <FolderOpen className="h-4 w-4" />
                      Seleccionar
                    </button>
                  )}
                  {material.location && (() => {
                    try {
                      const loc: { name: string; link: string } = JSON.parse(material.location);
                      return loc.link ? (
                        <a
                          href={loc.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-2 border rounded-lg hover:bg-muted transition-colors flex items-center shrink-0"
                          title="Obrir a Dropbox"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      ) : null;
                    } catch { return null; }
                  })()}
                  {material.location && (
                    <button
                      type="button"
                      onClick={() => setMaterial({ ...material, location: '' })}
                      className="px-2 py-2 border rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-red-500 shrink-0"
                      title="Eliminar carpeta"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Observacions */}
          <div className="space-y-2">
            <Label>Observacions</Label>
            <Textarea
              value={material.observations || ''}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setMaterial({ ...material, observations: e.target.value })
              }
              rows={3}
            />
          </div>
        </div>

        {/* Middle: Valors Calculats */}
        <div className="w-80 shrink-0 flex flex-col gap-2">
          <label className="block text-xs font-medium text-slate-700">Valors Calculats</label>
          <div className="bg-card border rounded-lg p-4 flex-1">
            <table className="w-full text-xs border-separate border-spacing-y-0.5">
              <colgroup>
                <col />
                <col className="w-16" />
                <col className="w-10" />
              </colgroup>
              <tbody>
                <tr>
                  <td className="text-muted-foreground">Pes calculat</td>
                  <td className="text-right tabular-nums font-medium">{fmtNum(calculatedWeight)}</td>
                  <td className="pl-1 text-muted-foreground">Kg/ut</td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Cost/Ut</td>
                  <td className="text-right tabular-nums font-medium">{fmtNum(calculatedCost)}</td>
                  <td className="pl-1 text-muted-foreground">€/Ut</td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Preu/Kg</td>
                  <td className="text-right tabular-nums font-medium">{fmtNum(calculatedPricePerKg)}</td>
                  <td className="pl-1 text-muted-foreground">€/kg</td>
                </tr>
                <tr>
                  <td colSpan={3} className="py-1"><hr className="border-dashed border-muted-foreground/30" /></td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Merma ({Number(material.wastePercentage || 0).toFixed(1)}%)</td>
                  <td className={`text-right tabular-nums font-medium ${wasteImpact > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {wasteImpact > 0 ? `+${fmtNum(wasteImpact)}` : '—'}
                  </td>
                  <td className={`pl-1 ${wasteImpact > 0 ? 'text-amber-600' : 'text-muted-foreground'}`}>
                    {wasteImpact > 0 ? '€' : ''}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted-foreground">Preu/Kg efectiu</td>
                  <td className="text-right tabular-nums font-medium">{fmtNum(pricePerKgWithWaste)}</td>
                  <td className="pl-1 text-muted-foreground">€/kg</td>
                </tr>
                <tr>
                  <td colSpan={3} className="py-1"><hr className="border-muted-foreground/30" /></td>
                </tr>
                <tr>
                  <td className="font-semibold">Preu / {material.unit || 'ut'}</td>
                  <td className="text-right tabular-nums font-bold text-primary">{fmtNum(pricePerMagnitude)}</td>
                  <td className="pl-1 font-semibold text-primary">€/{material.unit || 'ut'}</td>
                </tr>
              </tbody>
            </table>

            {priceHistory.length > 0 && (
              <div className="mt-4 pt-3 border-t border-dashed border-muted-foreground/20">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">Historial de preus</p>
                <table className="w-full text-[11px] border-separate border-spacing-y-0.5">
                  <colgroup>
                    <col />
                    <col className="w-16" />
                    <col className="w-12" />
                  </colgroup>
                  <tbody>
                    {priceHistory.map((h, i) => {
                      // Extreure YYYY-MM-DD sigui string ISO o objecte Date serialitzat
                      const fmtIso = (iso: string) => {
                        const ymd = iso.substring(0, 10); // "2026-07-20"
                        const [y, m, d] = ymd.split('-');
                        return `${d}/${m}/${y.substring(2)}`;
                      };
                      const displayDate = h.priceDate ? fmtIso(h.priceDate) : fmtIso(h.savedAt);
                      return (
                        <tr key={h.id} className={i === 0 ? 'text-foreground' : 'text-muted-foreground'}>
                          <td className="tabular-nums">{displayDate}</td>
                          <td className={`text-right tabular-nums font-medium ${i === 0 ? 'text-primary' : ''}`}>
                            {fmtNum(Number(h.pricePerMagnitude))}
                          </td>
                          <td className="pl-1">€/{h.unit}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Right: image */}
        <div className="w-96 min-w-[160px] flex flex-col gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ''; }}
          />
          <label className="block text-xs font-medium text-slate-700">Foto</label>
          {material.imageUrl ? (
            <div className="relative group rounded border overflow-hidden bg-slate-100">
              <img
                src={material.imageUrl}
                alt="Imatge material"
                className="w-full h-auto object-contain cursor-zoom-in"
                onClick={() => setLightboxOpen(true)}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button type="button" title="Veure a mida completa" onClick={() => setLightboxOpen(true)}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <ZoomIn className="h-4 w-4 text-slate-700" />
                </button>
                <button type="button" title="Substituir imatge" onClick={() => fileInputRef.current?.click()}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <Upload className="h-4 w-4 text-slate-700" />
                </button>
                <button type="button" title="Eliminar imatge"
                  onClick={() => setMaterial((prev) => ({ ...prev, imageUrl: null }))}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <X className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer transition-colors flex-1 ${
                imageDragOver ? 'border-teal-400 bg-teal-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
              }`}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setImageDragOver(true); }}
              onDragLeave={() => setImageDragOver(false)}
              onDrop={handleImageDrop}
            >
              {uploadingImage ? (
                <div className="text-slate-500 text-xs text-center px-2">Pujant...</div>
              ) : (
                <>
                  <Upload className="h-8 w-8 text-slate-300 mb-2" />
                  <div className="text-xs text-slate-500 text-center px-2">Arrossega o clica per afegir foto</div>
                  <div className="text-[10px] text-slate-400 mt-1">JPG · PNG · WEBP<br />màx. 10 MB</div>
                </>
              )}
            </div>
          )}

          {/* Fitxa tècnica / Plànol — segona imatge, independent de la foto */}
          <input
            ref={technicalFileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImageUpload(f, 'technicalImageUrl'); e.target.value = ''; }}
          />
          <label className="block text-xs font-medium text-slate-700">Fitxa tècnica / Plànol</label>
          {material.technicalImageUrl ? (
            <div className="relative group rounded border overflow-hidden bg-slate-100 h-36">
              <img
                src={material.technicalImageUrl}
                alt="Fitxa tècnica / plànol del material"
                className="w-full h-full object-contain cursor-zoom-in"
                onClick={() => setTechnicalLightboxOpen(true)}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                <button type="button" title="Veure a mida completa" onClick={() => setTechnicalLightboxOpen(true)}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <ZoomIn className="h-4 w-4 text-slate-700" />
                </button>
                <button type="button" title="Substituir imatge" onClick={() => technicalFileInputRef.current?.click()}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <Upload className="h-4 w-4 text-slate-700" />
                </button>
                <button type="button" title="Eliminar imatge"
                  onClick={() => setMaterial((prev) => ({ ...prev, technicalImageUrl: null }))}
                  className="bg-white/90 rounded-full p-2 hover:bg-white shadow">
                  <X className="h-4 w-4 text-red-600" />
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`border-2 border-dashed rounded flex flex-col items-center justify-center cursor-pointer transition-colors h-36 ${
                technicalImageDragOver ? 'border-teal-400 bg-teal-50' : 'border-slate-300 hover:border-slate-400 bg-slate-50'
              }`}
              onClick={() => technicalFileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setTechnicalImageDragOver(true); }}
              onDragLeave={() => setTechnicalImageDragOver(false)}
              onDrop={(e) => handleImageDrop(e, 'technicalImageUrl')}
            >
              {uploadingTechnicalImage ? (
                <div className="text-slate-500 text-xs text-center px-2">Pujant...</div>
              ) : (
                <>
                  <Upload className="h-6 w-6 text-slate-300 mb-1" />
                  <div className="text-xs text-slate-500 text-center px-2">Arrossega o clica per afegir fitxa tècnica</div>
                  <div className="text-[10px] text-slate-400 mt-1">JPG · PNG · WEBP<br />màx. 10 MB</div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      </CardContent></Card>}

      {/* Pestanya Escandalls */}
      {!isNew && activeTab === 'escandalls' && (
        <Card className="bg-muted/30 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b bg-muted/30">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Escandalls associats</h3>
            {nodeAssociations.length > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">{nodeAssociations.length} escandall{nodeAssociations.length !== 1 ? 's' : ''}</span>
            )}
          </div>
          {nodesLoading ? (
            <div className="py-6 text-center text-xs text-muted-foreground">Carregant...</div>
          ) : nodeAssociations.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">Aquest material no s'utilitza en cap escandall</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Codi</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Nom escandall</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Tipus</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Quantitat</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Amidament</th>
                    <th className="px-4 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {nodeAssociations.map((nl) => (
                    <tr key={nl.id} className="border-b last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2 font-mono text-muted-foreground">{nl.node.code ?? '—'}</td>
                      <td className="px-4 py-2 font-medium">{nl.node.name}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          nl.node.nodeType === 'ESCANDALL'
                            ? 'bg-purple-100 text-purple-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {nl.node.nodeType === 'ESCANDALL' ? 'Escandall' : 'Compost'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">{Number(nl.quantity).toFixed(4)} {nl.unit}</td>
                      <td className="px-4 py-2 text-right text-muted-foreground">{Number(nl.measurement).toFixed(4)}</td>
                      <td className="px-4 py-2 text-center">
                        <a
                          href={`/compres/nodes/${nl.node.id}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Obrir escandall"
                          className="text-muted-foreground hover:text-slate-700"
                        >
                          <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Pestanya Historial de compres */}
      {!isNew && activeTab === 'compres' && (
        <Card className="bg-muted/30 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b bg-muted/30">
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            <h3 className="text-sm font-medium">Historial de compres</h3>
            {purchaseHistory.length > 0 && (
              <span className="ml-auto text-xs text-muted-foreground">{purchaseHistory.length} línia{purchaseHistory.length !== 1 ? 'es' : ''}</span>
            )}
          </div>
          {historyLoading ? (
            <div className="py-8 text-center text-xs text-muted-foreground">Carregant...</div>
          ) : purchaseHistory.length === 0 ? (
            <div className="py-8 text-center text-xs text-muted-foreground">No hi ha compres registrades per a aquest material</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Comanda</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Data</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Proveïdor</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Quantitat</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Preu Unit.</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Dte %</th>
                    <th className="text-right px-4 py-2 font-medium text-muted-foreground">Total línia</th>
                    <th className="text-center px-4 py-2 font-medium text-muted-foreground">Estat</th>
                    <th className="px-4 py-2 w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseHistory.map((line) => {
                    const st = statusLabel[line.purchaseOrder.status] ?? { label: line.purchaseOrder.status, cls: 'bg-gray-100 text-gray-600' };
                    const dateStr = line.purchaseOrder.orderDate
                      ? new Date(line.purchaseOrder.orderDate).toLocaleDateString('ca-ES')
                      : '—';
                    return (
                      <tr key={line.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-2 font-mono font-medium">{line.purchaseOrder.orderNumber}</td>
                        <td className="px-4 py-2 text-muted-foreground">{dateStr}</td>
                        <td className="px-4 py-2">{line.purchaseOrder.supplier?.name ?? '—'}</td>
                        <td className="px-4 py-2 text-right">{Number(line.quantity).toFixed(2)} {line.unitType}</td>
                        <td className="px-4 py-2 text-right">{formatCurrency(line.unitPrice)} €</td>
                        <td className="px-4 py-2 text-right">{line.discountPercent > 0 ? `${line.discountPercent}%` : '—'}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(line.lineTotal)} €</td>
                        <td className="px-4 py-2 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <a
                            href={`/compres/comandes/${line.purchaseOrder.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Obrir comanda"
                            className="text-muted-foreground hover:text-slate-700"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                          </a>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Pestanya Documentació */}
      {!isNew && activeTab === 'documents' && (
        <Card className="bg-muted/30 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b bg-muted/30">
            <Paperclip className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-medium">Documentació</h3>
            {documents.length > 0 && (
              <span className="ml-auto text-xs font-normal text-muted-foreground">({documents.length})</span>
            )}
          </div>
          <div className="p-5">
            <label
              onDragOver={(e) => { e.preventDefault(); setDocsDragOver(true); }}
              onDragLeave={() => setDocsDragOver(false)}
              onDrop={handleDocumentsDrop}
              className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg py-4 cursor-pointer transition-colors ${
                docsDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Upload size={18} className="text-gray-400" />
              <span className="text-xs text-muted-foreground">
                {uploadingDoc ? 'Pujant...' : 'Arrossega o clica per pujar un document'}
              </span>
              <input
                type="file"
                className="hidden"
                disabled={uploadingDoc}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) handleUploadDocument(file);
                  e.target.value = '';
                }}
              />
            </label>

            {documents.length === 0 ? (
              <p className="text-muted-foreground text-xs text-center py-3">Encara no hi ha cap document</p>
            ) : (
              <div className="mt-3 space-y-1">
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-gray-50 text-xs">
                    <FileText size={14} className="text-gray-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate font-medium" title={doc.name}>{doc.name}</div>
                      <div className="text-muted-foreground">{fmtFileSize(doc.fileSize)} · {new Date(doc.createdAt).toLocaleDateString('ca-ES')}</div>
                    </div>
                    <button type="button" title="Visualitzar" onClick={() => handleOpenDocument(doc, false)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-blue-600 transition-colors">
                      <Eye size={14} />
                    </button>
                    <button type="button" title="Descarregar" onClick={() => handleOpenDocument(doc, true)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-green-600 transition-colors">
                      <Download size={14} />
                    </button>
                    <button type="button" title="Eliminar" onClick={() => handleDeleteDocument(doc.id)}
                      className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Pestanya Arbre d'elements */}
      {!isNew && activeTab === 'arbre' && (
        <Card className="bg-muted/30 overflow-hidden">
          <div className="flex items-center gap-2 px-5 py-3 border-b bg-muted/30">
            <FolderTree className="h-4 w-4 text-indigo-600" />
            <h3 className="text-sm font-medium">Arbre d'elements</h3>
            <span className="ml-auto text-xs text-muted-foreground">
              Tipus d'Elements › Família d'Elements › Tipus de Producte
            </span>
          </div>
          <div className="p-5">
            {codeTreeLoading ? (
              <p className="text-xs text-muted-foreground text-center py-6">Carregant...</p>
            ) : !codeTree ? (
              <p className="text-xs text-muted-foreground text-center py-6">No s'ha pogut carregar l'arbre.</p>
            ) : (
              <div className="space-y-2">
                {codeTree.elementTypes.map((et) => {
                  const etOpen = expandedElementTypes.has(et.id);
                  const etCurrent = material.elementTypeId === et.id;
                  const childMaterialTypes = codeTree.materialTypes.filter((mt) => mt.elementTypeId === et.id);
                  return (
                    <div key={et.id}>
                      <button
                        type="button"
                        onClick={() => setExpandedElementTypes((prev) => {
                          const next = new Set(prev);
                          if (next.has(et.id)) next.delete(et.id); else next.add(et.id);
                          return next;
                        })}
                        className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg border text-left transition-colors ${
                          etCurrent ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        {etOpen ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                        <span className="font-mono text-xs text-muted-foreground shrink-0">{et.id}</span>
                        <span className="text-xs font-semibold">{et.name}</span>
                        {etCurrent && <span className="ml-auto text-[10px] font-medium text-indigo-600 shrink-0">Aquest element</span>}
                      </button>

                      {etOpen && (
                        <div className="ml-5 mt-1.5 pl-3 border-l-2 border-gray-100 space-y-1.5">
                          {childMaterialTypes.length === 0 ? (
                            <p className="text-[11px] text-muted-foreground py-1">Sense famílies associades.</p>
                          ) : childMaterialTypes.map((mt) => {
                            const mtOpen = expandedMaterialTypes.has(mt.id);
                            const mtCurrent = material.materialTypeId === mt.id;
                            const childCombos = codeTree.combos.filter((c) => c.elementTypeId === et.id && c.materialTypeId === mt.id);
                            return (
                              <div key={mt.id}>
                                <button
                                  type="button"
                                  onClick={() => setExpandedMaterialTypes((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(mt.id)) next.delete(mt.id); else next.add(mt.id);
                                    return next;
                                  })}
                                  className={`flex items-center gap-2 w-full px-3 py-1.5 rounded-lg border text-left transition-colors ${
                                    mtCurrent ? 'border-teal-500 bg-teal-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                                  }`}
                                >
                                  {mtOpen ? <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" /> : <ChevronRight className="h-3 w-3 shrink-0 text-muted-foreground" />}
                                  <span className="font-mono text-[11px] text-muted-foreground shrink-0">{mt.code}</span>
                                  <span className="text-xs">{mt.name}</span>
                                  {mtCurrent && <span className="ml-auto text-[10px] font-medium text-teal-600 shrink-0">Aquest element</span>}
                                </button>

                                {mtOpen && (
                                  <div className="ml-5 mt-1.5 pl-3 border-l-2 border-gray-100 flex flex-wrap gap-1.5 pb-1">
                                    {childCombos.length === 0 ? (
                                      <p className="text-[11px] text-muted-foreground py-1">Sense productes associats encara.</p>
                                    ) : childCombos.map((c) => {
                                      const ccCurrent = material.compositeCodeId === c.compositeCodeId;
                                      return (
                                        <div
                                          key={c.compositeCodeId}
                                          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-[11px] ${
                                            ccCurrent ? 'border-purple-500 bg-purple-50' : 'border-gray-200 bg-white'
                                          }`}
                                        >
                                          <span className="font-mono text-muted-foreground">{c.compositeCode}</span>
                                          <span>{c.compositeCodeName}</span>
                                          <span className="text-muted-foreground">({c.count})</span>
                                          {ccCurrent && <span className="font-medium text-purple-600">· Aquest</span>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Lightbox */}
      {lightboxOpen && material.imageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <img
            src={material.imageUrl}
            alt="Imatge material"
            className="max-w-full max-h-full object-contain rounded shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      )}

      {technicalLightboxOpen && material.technicalImageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setTechnicalLightboxOpen(false)}
        >
          <img
            src={material.technicalImageUrl}
            alt="Fitxa tècnica / plànol del material"
            className="max-w-full max-h-full object-contain rounded shadow-xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            className="absolute top-4 right-4 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white"
            onClick={() => setTechnicalLightboxOpen(false)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>
      )}
    </div>
  );
}
